import { GPickResourceScrollable } from './GPickResourceScrollable';
import { DEFAULT_CANCEL_CONTENT } from './constants';
import { GModal } from '~/presentation/common';
import { useCallback } from 'react';

interface IConfirmAction {
  content: string;
  onAction: () => void;
  disabled?: boolean;
  loading?: boolean;
}

interface IProps {
  open: boolean;
  title: string | React.ReactNode;
  onClose: () => void;
  confirm: IConfirmAction;
  cancelContent?: string;
  isLoading?: boolean;
  // Rendered in the footer, beside the actions
  footer?: React.ReactNode;
  children: React.ReactNode;
}

// Shared shell for pick-resource modals (template picker, page picker, ...): a large,
// no-scroll GModal with a primary confirm action and a cancel action that is blocked
// while a confirm is in flight. Compose the body with `GPickResourceModal.Scrollable`.
function GPickResourceModalRoot({
  open,
  title,
  onClose,
  confirm,
  cancelContent = DEFAULT_CANCEL_CONTENT,
  isLoading = false,
  footer,
  children,
}: IProps) {
  const handleClose = useCallback(() => {
    if (isLoading) return;
    onClose();
  }, [isLoading, onClose]);

  return (
    <GModal
      size="large"
      noScroll
      open={open}
      title={title}
      onClose={handleClose}
      footer={footer}
      primaryAction={{
        content: confirm.content,
        loading: confirm.loading,
        disabled: confirm.disabled,
        onAction: confirm.onAction,
      }}
      secondaryActions={[
        {
          content: cancelContent,
          disabled: isLoading,
          onAction: handleClose,
        },
      ]}
    >
      {children}
    </GModal>
  );
}

export const GPickResourceModal = Object.assign(GPickResourceModalRoot, {
  Scrollable: GPickResourceScrollable,
});
